import React, { useEffect, useState } from 'react';
import { Box, Checkbox, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { AgentCatalog, AgentSelection, benchmarkService } from '../../services/benchmarkService';
import { dsVar, ink } from '../../theme/tokens';

interface AgentSelectionPickerProps {
    /** The flow to be run: a production flow or its BENCHMARK_/VALIDATE_ companion. */
    flowCode: string;
    /** Null runs every agent the flow has, in the flow's own default mode. */
    value: AgentSelection | null;
    onChange: (selection: AgentSelection | null) => void;
    /** Compact wording for a narrow panel. */
    dense?: boolean;
}

const chipSx = (color: string) => ({
    display: 'inline-flex', px: 0.75, borderRadius: '999px', fontSize: '10px', fontWeight: 600,
    color: ink(color), backgroundColor: `${color}1A`, border: `1px solid ${color}4D`, whiteSpace: 'nowrap' as const
});

/**
 * Which specialist agents a run should use, chosen from the flow's own catalogue
 * (GET /benchmarks/agent-catalog). Only those agents run, and on a benchmark only their
 * risk factors are compared. A flow with no supervised investigation (prioritisation)
 * has nothing to choose, and the picker shows nothing.
 *
 * Mode: `exact` runs each selected agent once, which is what a benchmark needs to measure
 * an agent in isolation; `restrict` lets the supervisor route among them. On a live EDD
 * review mandatory checks are always kept, so the picker says which those are.
 */
const AgentSelectionPicker: React.FC<AgentSelectionPickerProps> = ({ flowCode, value, onChange, dense }) => {
    const [catalog, setCatalog] = useState<AgentCatalog | null>(null);
    const [unavailable, setUnavailable] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setCatalog(null);
        setUnavailable(false);
        if (!flowCode) return () => { cancelled = true; };
        benchmarkService.getAgentCatalog(flowCode)
            .then((data) => { if (!cancelled) setCatalog(data); })
            .catch(() => { if (!cancelled) setUnavailable(true); });
        return () => { cancelled = true; };
    }, [flowCode]);

    if (unavailable || !catalog) return null;
    const agents = catalog.agents.filter((a) => a.inFlow);
    if (!agents.length) return null;

    const selected = new Set(value?.agents ?? agents.map((a) => a.agent));
    const mode = (value?.mode as 'exact' | 'restrict') ?? catalog.defaultMode;
    const emit = (next: Set<string>, nextMode = mode) => {
        // Every agent in the flow's default mode is the same as no selection: send none.
        if (next.size === agents.length && nextMode === catalog.defaultMode) onChange(null);
        else onChange({ agents: agents.filter((a) => next.has(a.agent)).map((a) => a.agent), mode: nextMode });
    };
    const toggle = (agent: string) => {
        const next = new Set(selected);
        if (next.has(agent)) next.delete(agent);
        else next.add(agent);
        if (next.size) emit(next);
    };
    const live = !/^(BENCHMARK|VALIDATE)_/.test(flowCode);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: dsVar('fg-soft'), letterSpacing: '0.02em' }}>
                    {catalog.stage === 'edd' ? 'Checks to run' : 'Agents to run'} · {selected.size} of {agents.length}
                </Typography>
                <Select
                    size="small"
                    value={mode}
                    onChange={(e) => emit(selected, e.target.value as 'exact' | 'restrict')}
                    aria-label="Selection mode"
                    sx={{ fontSize: '11px', height: 26, minWidth: dense ? 96 : 120 }}
                >
                    <MenuItem value="exact" sx={{ fontSize: '12px' }}>Exact</MenuItem>
                    <MenuItem value="restrict" sx={{ fontSize: '12px' }}>Restrict</MenuItem>
                </Select>
            </Box>
            <Typography sx={{ fontSize: '10.5px', color: dsVar('fg-muted'), lineHeight: 1.5 }}>
                {mode === 'exact'
                    ? 'Each selected agent runs once, with no model routing'
                    : 'The supervisor routes among the selected agents'}
                {live && catalog.stage === 'edd' ? '. On a live review, mandatory checks always run.' : '.'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: dense ? 220 : 320, overflowY: 'auto' }}>
                {agents.map((a) => (
                    <Box
                        key={a.agent}
                        component="label"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.125, cursor: 'pointer' }}
                    >
                        <Checkbox
                            size="small"
                            checked={selected.has(a.agent)}
                            onChange={() => toggle(a.agent)}
                            disabled={selected.has(a.agent) && selected.size === 1}
                            sx={{ p: 0.5 }}
                            inputProps={{ 'aria-label': a.label }}
                        />
                        <Typography sx={{ fontSize: '12px', color: dsVar('fg-body'), flex: 1, minWidth: 0 }}>{a.label}</Typography>
                        {a.mandatory && (
                            <Tooltip arrow title={`Kept on every live review${a.mandatoryFor.length ? ` of ${a.mandatoryFor.join(', ')}s` : ''}; a benchmark may leave it out, and says so on the run.`}>
                                <Box component="span" sx={chipSx('#FBBF24')}>Mandatory</Box>
                            </Tooltip>
                        )}
                        {a.external && (
                            <Tooltip arrow title="Reads today's watchlists, news, registries or websites. On a benchmark of past cases its findings are look-ahead, or withheld unless the loader allows live checks.">
                                <Box component="span" sx={chipSx('#94A3B8')}>Live sources</Box>
                            </Tooltip>
                        )}
                    </Box>
                ))}
            </Box>
            {catalog.alwaysCompared.length > 0 && (
                <Typography sx={{ fontSize: '10.5px', color: dsVar('fg-muted') }}>
                    Always compared: the declared KYC profile ({catalog.alwaysCompared.reduce((n, a) => n + a.factors.length, 0)} factors).
                </Typography>
            )}
        </Box>
    );
};

export default AgentSelectionPicker;
